/**
 * Saved "Fusion case" — a lightweight, CLIENT-SIDE snapshot of the /fusion demo canvas
 * (localStorage). Pressing Save captures the current elements + positions so the canvas
 * survives a reload and repeated Build↔Canvas tab switches (the demo re-seeds on each
 * mount, so runtime-added intake nodes would otherwise be lost).
 *
 * Scope on purpose: the DEMO is synthetic, so its state stays on the client — it does NOT
 * graduate into a real backend case (real cases persist to case_entities server-side). One
 * slot, unnamed — the "for now" pragmatic fix.
 */
import type cytoscape from 'cytoscape';
import type { ElementDefinition } from 'cytoscape';

const KEY = 'fusion-case:saved';

export interface SavedFusionCase {
  savedAt: number;
  nodeCount: number;
  elements: ElementDefinition[];
}

/** Serialize the live canvas to {group, data, position} — enough to rebuild it exactly. */
export function saveFusionCase(cy: cytoscape.Core): SavedFusionCase | null {
  try {
    const elements = cy.elements().map((el) => ({
      group: el.isNode() ? 'nodes' : 'edges',
      data: { ...el.data() },
      position: el.isNode() ? { ...el.position() } : undefined,
    })) as ElementDefinition[];
    const payload: SavedFusionCase = { savedAt: Date.now(), nodeCount: cy.nodes().length, elements };
    localStorage.setItem(KEY, JSON.stringify(payload));
    return payload;
  } catch {
    return null;   // quota / serialization — fail soft
  }
}

export function loadFusionCase(): SavedFusionCase | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as SavedFusionCase;
    return Array.isArray(p?.elements) && p.elements.length ? p : null;
  } catch {
    return null;
  }
}

export function clearFusionCase(): void {
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
}

export function hasFusionCase(): boolean {
  return !!loadFusionCase();
}
